//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { LocationsMassGroupsEditService } from '../services/locations-mass-groups-edit'
import { LocationsMassGroupsEditErrorsSummaryService } from '../services/locations-mass-groups-edit'
import { UserSessionService } from '../../../services/user-session'
import { LocationService } from '../../../services/location'
import { ILocationsMassGroupsEdit, LocationsMassGroupsEditOptions, LocationsMassGroupsAssigningProgressStep } from './locations-mass-groups-edit.interfaces'
import { TypeHelper, LocalizationHelper, LogHelper, ConvertHelper } from '../../../helpers'
import { GridOptions, ButtonsOptions, GridTypes, GridController } from '../../common/grid/grid.controller'
import { IMRErrorsSummaryOptions, IIMRErrorsSummary, SummaryOptions, ErrorsSummaryResult } from '../../common/errors-summary'
import { Enums, Activity } from '../../../models/common'
import { ColumnModel } from '../../api/api.models'
import { OpView, OpLocation, OpViewColumn } from '../../../models/core'
import { ItemSwapperOptions, IItemSwapper, ItemSwapperResult } from '../../common/item-swapper'
import { ColumnInfo } from '../../../models/custom'
import { LocationMassGroupsAssigningErrorCode } from './locations-mass-groups-edit.interfaces'
import { GridService } from '../../../services/grid'

export class LocationsMassGroupsEditController
    implements ng.IComponentController
    , ILocationsMassGroupsEdit {
    // #region Consts

    public get VALIDATION_WARNING_POPUP_ID(): string { return 'VALIDATION_WARNING_POPUP_ID' }

    private static get LOCATION_MASS_GROUPS_EDIT_GRID_NAME(): string { return 'LocationsMassGroupsEditGrid' }

    private static get ERROR_MARKER(): string { return '' }

    private static get SUCCESS_TEXT(): string { return 'successText' }
    private static get ERROR_TEXT(): string { return 'errorText' }
    private static get WARNING_TEXT(): string { return 'warningText' }

    private static get SUCCESS_COLOR(): string { return '#02E6A1' }
    private static get ERROR_COLOR(): string { return '#F7484E' }
    private static get WARNING_COLOR(): string { return '#FFA500' }

    // #endregion

    // #region Localization

    public get SavingSummaryText(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SavingSummary) }

    // #endregion

    // #region Members

    public id: number
    public options: LocationsMassGroupsEditOptions

    public isLoading: boolean
    public isPopupReady: boolean

    public validationWarningConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }

    public errorsSummaryOptions: IMRErrorsSummaryOptions
    public itemSwapperOptions: ItemSwapperOptions<OpLocation>
    public locationGridOptions: GridOptions
    public savingProgressOptions: DevExpress.ui.dxProgressBarOptions
    public startButtonOptions: DevExpress.ui.dxButtonOptions
    public stopButtonOptions: DevExpress.ui.dxButtonOptions
    public finishButtonOptions: DevExpress.ui.dxButtonOptions
    public loadIndicatorOptions: DevExpress.ui.dxLoadIndicatorOptions
    public popupOptions: IMRPopupOptions

    private popup: IIMRPopup
    private grid: DevExpress.ui.dxDataGrid
    private errorsSummaryComponent: IIMRErrorsSummary
    private itemSwapper: IItemSwapper<OpLocation>
    private gridController: GridController<OpLocation>
    private startProcessButton: DevExpress.ui.dxButton
    private stopButton: DevExpress.ui.dxButton
    private finishButton: DevExpress.ui.dxButton

    public errorsSummaryVisible: boolean
    public isStarted: boolean

    private view: OpView

    private savingStartTime: number
    private savingEndTime: number
    private savedLocationsCount: number

    private hasErrors: boolean
    private isPopupClosing: boolean

    public uploadedPercent: number // used by binding, do not remove even if IDE tips its not used

    private currentProgressStep: LocationsMassGroupsAssigningProgressStep
    private massGroupAssigningSummaryResult: ErrorsSummaryResult[] = []
    private locations: OpLocation[]

    // #endregion

    // #region Ctor

    constructor(
        private userSessionService: UserSessionService,
        private massLocationGroupsEditService: LocationsMassGroupsEditService,
        private massLocationGroupsEditErrorsSummaryService: LocationsMassGroupsEditErrorsSummaryService,
        private locationService: LocationService,
        private gridService: GridService
    ) {

    }

    // #endregion

    // #region ng.IComponentController

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region Setup

    private initialize(): void {
        this.isLoading = true

        this.setPopupOptions()
            .then(() => {
                this.userSessionService
                    .getDataProvider()
                    .View
                    .getActiveView(LocationService.REFERENCE_TYPE)
                    .then((view: OpView) => {
                        this.setItemSwapperOptions(view)
                        this.setOptions()
                        this.setLoadIndicatorOptions()
                        this.setSavingProgressOptions()
                        this.setConfirmationOptions()
                        this.initializeErrorsSummary()
                    })
            })
            .then(() => {

                return this.setGridOptions()
            })
            .then(() => {
                this.isLoading = false
                this.onInitialized()
            })
    }

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,
            toggleDetailsRowsButtonVisible: true,
            onToggleDetailsRows: (isExpanded: boolean): void => { this.onGridToggleDetailsRows(isExpanded) }
        }
    }

    private getGridMasterDetailOptions(detailsRowsVisibleState: boolean): any {
        return {
            enabled: true,
            template: function (container: JQuery, options: any) {
                const error: any = options.data[LocationsMassGroupsEditController.ERROR_TEXT];
                if (error != null) {
                    container.append($('<div ><p>' + error + '</p></div>'));
                }

                const warning: any = options.data[LocationsMassGroupsEditController.WARNING_TEXT];
                if (warning != null) {
                    container.append($('<div ><p>' + warning + '</p></div>'));
                }
            },
            autoExpandAll: detailsRowsVisibleState
        }
    }

    private getGridEditingOptions(): any {
        return {
            mode: 'cell',
            allowAdding: false,
            allowUpdating: false,
            allowDeleting: false
        }
    }

    private setOptions(): void {
        this.errorsSummaryVisible = false
        this.isStarted = false

        this.savingStartTime = 0
        this.savingEndTime = 0
        this.savedLocationsCount = 0
        this.uploadedPercent = 0

        this.hasErrors = false
        this.isPopupClosing = false

        this.currentProgressStep = null
        this.massGroupAssigningSummaryResult = []
    }

    private setPopupOptions(): JQueryPromise<void> {
        this.popupOptions = {
            bottomToolbarOptions: {
                innerOptions: {
                    items: [
                        {
                            location: 'after',
                            widget: 'dxButton',
                            options: {
                                disabled: this.isStarted,
                                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartProcess),
                                onClick: () => { this.onSaveEntitiesClick() },
                                onInitialized: (args: any) => { this.onStartProcessButtonInitialized(args) }
                            }
                        },
                        {
                            location: 'after',
                            widget: 'dxButton',
                            options: {
                                disabled: !this.isStarted,
                                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Stop),
                                onClick: () => { this.onStopSavingClick() },
                                onInitialized: (args: any) => { this.onStopButtonInitialized(args) }
                            }
                        },
                        {
                            location: 'after',
                            widget: 'dxButton',
                            options: {
                                disabled: this.isStarted,
                                text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Finish),
                                onClick: () => { this.onFinishSavingClick() },
                                onInitialized: (args: any) => { this.onFinishButtonInitialized(args) }
                            }
                        },
                    ],
                }
            },

            innerOptions: {
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.GroupsEdit),

                width: '100%',
                height: '100%',

                onInitialized: (args: any) => { this.onPopupInitialized(args) },
                onHiding: (args: any) => { this.onPopupHiding(args) },
                onHidden: () => { this.onPopupHidden() },
                onShown: (args: any) => { this.onPopupShown() }
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.onPopupInitialized(e.component)
            }
        }

        return TypeHelper.onInitializedAll(this.popupOptions)
    }

    private setGridOptions(): JQueryPromise<void> {
        return $.when<any>(
            this.userSessionService
                .getDataProvider()
                .System
                .getRequestColumnInfo(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS, this.options.moduleName),
            this.userSessionService
                .getDataProvider()
                .System
                .getRequestColumnInfo(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS, this.options.moduleName)
        )
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {

                const detailsRowsVisibleState: boolean = this.massLocationGroupsEditService.loadDetailsRowsVisibleState()

                this.locationGridOptions = <any>{
                    columnAutoWidth: true,
                    addCustomFilters: false,
                    ignoreKey: true,
                    additionalTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ChoosenElements),
                    columnsDictionary: columns,
                    dataSourceParser: this.locationService.getMainGridDataSourceParser(),
                    defaultColumnsDictionary: defaultColumns,
                    exportOptions: null,
                    importOptions: null,
                    idReferenceType: this.options.referenceType,
                    localStorageVarForSavedEntity: null,
                    name: LocationsMassGroupsEditController.LOCATION_MASS_GROUPS_EDIT_GRID_NAME,
                    patternsProviderOptions: null,
                    preExecuteClearFilter: null,
                    referenceId: this.options.referenceType,
                    type: GridTypes.PAGINATED_SMALL,

                    dataSource: this.locations,

                    allowColumnReordering: !this.userSessionService
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.LocationGroups),

                    editing: this.getGridEditingOptions(),
                    masterDetail: this.getGridMasterDetailOptions(detailsRowsVisibleState),
                    buttonsOptions: this.getGridButtonsOptions(),

                    onInitialized: (args: any, grid: GridController<OpLocation>) => { this.onGridInitialized(args, grid) },
                    onRowPrepared: (args: any) => { this.onGridRowPrepared(args, detailsRowsVisibleState) },
                }
            })
    }

    private setSavingProgressOptions(): void {
        this.savingProgressOptions = {
            visible: true,
            value: 0,
            min: 0,
            max: 100,
            statusFormat: (ratio: any, value: number) => { return this.getProgressBarStatus(ratio, value); },
            bindingOptions: {
                value: '$ctrl.uploadedPercent'
            },
        }
    }

    private setItemSwapperOptions(view: OpView): void {
        const calculateCellValueFunc: (data: any) => any = (data: any): any => {
            return data == null
                ? TypeHelper.emptyString
                : this.gridService.getSourceObjectFromGridObject(data, this.options.referenceType)
        }

        const idLocationColumn: OpViewColumn = _.find(view.columns, (column: OpViewColumn) => column.idDataType == Enums.DataType.LOCATION_NAME)
        const columnAvailableGroups: ColumnModel = new ColumnModel()
        columnAvailableGroups.idName = idLocationColumn.name
        columnAvailableGroups.name = 'availableGroups'
        columnAvailableGroups.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AvailableGroups)
        columnAvailableGroups.type = Enums.DataTypeClass.Text
        columnAvailableGroups.calculateCellValue = calculateCellValueFunc

        const columnAssignedGroups: ColumnModel = new ColumnModel()
        columnAssignedGroups.idName = 'idLocation'
        columnAssignedGroups.name = 'assignedGroups'
        columnAssignedGroups.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedGroups)
        columnAssignedGroups.type = Enums.DataTypeClass.Text
        columnAssignedGroups.calculateCellValue = calculateCellValueFunc

        this.itemSwapperOptions = {
            referenceType: this.options.referenceType,

            rightGridOptions: {
                columnsDictionary: [columnAssignedGroups],

                getDataSource: () => { return this.getLocationAssignedGroups() },
            },

            leftGridOptions: {
                columnsDictionary: [columnAvailableGroups],

                dataBag: true,

                getDataSourceFilter: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
                    if (view != null) {
                        columns = view.columns.map((viewColumn: OpViewColumn) => {
                            const columnInfo: ColumnInfo = new ColumnInfo(null)
                            columnInfo.IdDataType = viewColumn.idDataType
                            columnInfo.Name = viewColumn.name

                            return columnInfo
                        })

                        return this.locationService.getAvailableParentLocations(view, columns, pageNbr, pageSize, customWhereClause)
                    }

                    return $.Deferred<any>().resolve({
                        objects: [],
                        totalCount: 0,
                        error: null
                    }).promise()
                },
            },

            onInitialized: (itemSwapper: IItemSwapper<OpLocation>) => { this.onLocationGroupsItemSwapperInitialized(itemSwapper) },
        }
    }

    private setConfirmationOptions(): void {
        this.validationWarningConfirmationOptions = {
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfirmChanges),
            onConfirmed: () => { this.saveLocations() }
        }
    }

    private setLoadIndicatorOptions(): void {
        this.loadIndicatorOptions = {
            height: 25,
            width: 25
        }
    }

    // #endregion

    // #region Events

    private onInitialized(): void {
        if (this.options != null && this.options.onInitialized != null) {
            this.options.onInitialized(this)
        }
    }

    private onGridInitialized(args: any, controller: GridController<OpLocation>): void {
        this.grid = args.component;
        this.grid.option({ columnMinWidth: undefined });

        this.gridController = controller

        this.cancelSaving();
    }

    private onGridRowPrepared(args: any, detailsRowsVisibleState: boolean): void {
        if (args.rowType == "data") {
            const row: JQuery = $(args.rowElement)

            let color: string = null

            if (!TypeHelper.isNullOrEmpty(args.data.successText)) {
                color = LocationsMassGroupsEditController.SUCCESS_COLOR
            } else if (!TypeHelper.isNullOrEmpty(args.data.errorText)) {
                args.isExpanded = detailsRowsVisibleState
                color = LocationsMassGroupsEditController.ERROR_COLOR
            } else if (!TypeHelper.isNullOrEmpty(args.data.warningText)) {
                args.isExpanded = detailsRowsVisibleState
                color = LocationsMassGroupsEditController.WARNING_COLOR
            }

            row.css('background-color', color)
            row.children('td').css('background-color', 'inherit')
        }
    }

    private onGridToggleDetailsRows(isExpanded: boolean): void {
        this.massLocationGroupsEditService.saveDetailsRowsVisibleState(isExpanded);
    }

    private onSaveEntitiesClick(): void {
        if ((this.locations || []).length == this.savedLocationsCount || this.hasErrors) {
            this.cancelSaving()
        }

        this.setButtonsIsDisabled(true, false, true);

        this.saveLocations()
    }

    private onStopSavingClick(): void {
        this.isStarted = false

        this.setButtonsIsDisabled(false, true, false);
    }

    private onFinishSavingClick(): void {
        this.hide()
    }

    private onPopupInitialized(component: IIMRPopup): void {
        this.popup = component
    }

    private onPopupHidden(): void {

    }

    private onPopupHiding(args: any): void {
        this.hide()
    }

    private onPopupShown(): void {
        this.isPopupReady = true
        this.isLoading = false

        this.gridController.setDataSource(this.locations)
    }

    private onLocationGroupsItemSwapperInitialized(itemSwapper: IItemSwapper<OpLocation>): void {
        this.itemSwapper = itemSwapper
    }

    private onStartProcessButtonInitialized(args: any): void {
        this.startProcessButton = args.component
    }

    private onStopButtonInitialized(args: any): void {
        this.stopButton = args.component
    }

    private onFinishButtonInitialized(args: any): void {
        this.finishButton = args.component
    }

    // #endregion

    // #region ILocationsMassGroupsEdit

    public show(): void {
        this.isPopupClosing = false
        this.isPopupReady = false

        this.popup.show()
    }

    public hide(): void {
        this.reset()

        this.popup.hide()
    }

    public setLocations(locations: OpLocation[]): void {
        this.locations = locations

        this.updatePopupTitle()
    }

    // #endregion

    // #region LocationsMassGroupsEditController

    private setButtonsIsDisabled(startIsDisabled: boolean, stopIsDisabled: boolean, finishIsDisabled: boolean): void {
        if (this.startProcessButton != null) {
            this.startProcessButton.option('disabled', startIsDisabled)
        }

        if (this.stopButton != null) {
            this.stopButton.option('disabled', stopIsDisabled)
        }

        if (this.finishButton != null) {
            this.finishButton.option('disabled', finishIsDisabled)
        }
    }

    private getLocationAssignedGroups(): JQueryPromise<OpLocation[]> {
        const idLocations: number[] = (this.locations || []).map((location: OpLocation) => location.idLocation)

        if (idLocations.length > 0) {
            return this.userSessionService
                .getDataProvider()
                .Location
                .getLocationAssignedGroup(idLocations, true)
        }

        return $.Deferred<OpLocation[]>().resolve([]).promise()
    }

    private getProgressBarStatus(ratio: any, value: number): string {
        const calculatedValue: number = Math.round(ratio * 100);
        return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Progress)}: ${calculatedValue}%`;
    }

    private saveLocations(): JQueryPromise<void> {
        this.resetSaveProgress()
        this.isStarted = true
        const itemSwapperResult: ItemSwapperResult<OpLocation> = this.itemSwapper.getSwapResult()

        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        let promise: JQueryPromise<void> = deferred.promise()

        this.locations
            .forEach((editedLocation: OpLocation) => {

                if (this.isPopupClosing) {
                    return
                }

                const itemDeferred: JQueryDeferred<void> = $.Deferred<void>()

                promise = promise.then(() => {
                    if (this.isPopupClosing || !this.isStarted) {
                        return $.Deferred<void>().resolve().promise()
                    }

                    if (this.isStarted) {
                        this.currentProgressStep = new LocationsMassGroupsAssigningProgressStep(this.currentProgressStep)

                        this.userSessionService
                            .getDataProvider()
                            .Location
                            .getLocation(editedLocation.idLocation)
                            .then((location: OpLocation) => {
                                if (location != null) {
                                    location.parentGroups = location.parentGroups || []

                                    if (_.size(location.parentGroups) > 0) {
                                        // Usuwamy parenty tylko jeśli w ogóle jakieś mamy
                                        itemSwapperResult.addedToLeft.forEach((removed: OpLocation) => {
                                            const toRemove: OpLocation = _.find(location.parentGroups, (existingParent: OpLocation) => existingParent.idLocation === removed.idLocation)
                                            if (toRemove != null) {
                                                toRemove.opState = Enums.OpChangeState.Delete
                                            }
                                        })
                                    }

                                    itemSwapperResult.addedToRight.forEach((added: OpLocation) => {
                                        // Jeśli w obecnej kolekcji nie znaleźliśmy analizowanego parenta to go dodajemy
                                        let toAdd: OpLocation = _.find(location.parentGroups, (existingParent: OpLocation) => existingParent.idLocation === added.idLocation)
                                        if (toAdd == null) {
                                            toAdd = new OpLocation(null)
                                            toAdd.idLocation = added.idLocation
                                            toAdd.opState = Enums.OpChangeState.New

                                            location.parentGroups.push(toAdd)
                                        }
                                    })
                                }

                                this.userSessionService
                                    .getDataProvider()
                                    .Location
                                    .saveLocation(location)
                                    .then((saved: OpLocation) => {
                                        this.currentProgressStep.itemsCount++
                                        this.currentProgressStep.successItemsCount++

                                        this.setRowResult(this.savedLocationsCount, LocationsMassGroupsEditController.SUCCESS_TEXT, LocationsMassGroupsEditController.SUCCESS_TEXT)
                                    })
                                    .fail((error: any) => {
                                        this.saveLocationFailed(error, location);
                                    })
                                    .always(() => {
                                        this.savedLocationsCount++
                                        this.calculatedUploadProgress()
                                        itemDeferred.resolve()
                                    })
                            })
                            .fail((error: any) => {
                                this.saveLocationFailed(error, editedLocation)

                                this.savedLocationsCount++
                                this.calculatedUploadProgress()

                                itemDeferred.resolve()
                            })

                        return itemDeferred.promise()
                    }

                    return $.Deferred<void>().resolve().promise()
                })
            })

        promise.always(() => {
            if (_.isFunction(this.options.onSaved)) {
                this.options.onSaved()
            }

            this.isStarted = false
            this.savingEndTime = performance.now()

            this.setSummaryOptions({
                parameters: this.massLocationGroupsEditErrorsSummaryService.createErrorsSummaryParameters(this.currentProgressStep),
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.MassGroupAssigningSummary),
                duration: this.savingEndTime - this.savingStartTime,
                summaryResult: this.massGroupAssigningSummaryResult,
                errorMarker: LocationsMassGroupsEditController.ERROR_MARKER
            })

            this.showErrorsSummarySection()

            if (this.itemSwapper != null) {
                this.itemSwapper.reset()
            }

            this.setButtonsIsDisabled(false, true, false);
        })

        deferred.resolve()

        return promise
    }

    private saveLocationFailed(error: any, location: OpLocation): void {
        const errorMsg: string = ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator);

        LogHelper.error(LocationService.LOG_PREFIX, errorMsg);

        if (error != null) {
            this.currentProgressStep.itemsCount++;
            this.currentProgressStep.errorItemsCount++;
            this.setRowResult(this.savedLocationsCount, LocationsMassGroupsEditController.ERROR_TEXT, errorMsg, true);
            const status: string = LocalizationHelper.getLocalizedEnum<LocationMassGroupsAssigningErrorCode>(LocationMassGroupsAssigningErrorCode, LocationMassGroupsAssigningErrorCode.Failed);
            this.massGroupAssigningSummaryResult.push({
                key: location.idLocation,
                isVisible: true,
                description: errorMsg,
                status: status
            });
        } else {
            this.setRowResult(this.savedLocationsCount, LocationsMassGroupsEditController.ERROR_TEXT, LocalizationHelper.getLocalizedText(LocalizationHelper.Text.WebServiceException), true);
        }
    }

    private cancelSaving(): void {
        this.isStarted = false
    }

    private setRowResult(rowIndex: any, validationStatus: string, message: string, expandRow: boolean = false): void {
        if (this.grid == null) {
            LogHelper.warn(LocationService.LOG_PREFIX, 'Attempted to set row result but GRID does not exist.')
        } else {
            const rows: any[] = this.grid.getDataSource().items()
            if (rowIndex < _.size(rows)) {
                const row: any = rows[rowIndex]

                if (row == null) {
                    LogHelper.warn(LocationService.LOG_PREFIX, 'Attempted to set row result but ROW does not exist.')
                } else {
                    row[validationStatus] = message

                    if (expandRow) {
                        this.grid.expandRow(row)
                    }

                    this.grid.repaintRows([rowIndex])
                }
            }
        }
    }

    private calculatedUploadProgress(): void {
        let locationsSize: number = _.size(this.locations)
        if (locationsSize < 1) {
            locationsSize = 1
        }

        this.uploadedPercent = ((this.savedLocationsCount * 100) / locationsSize)
    }

    private updatePopupTitle(): void {
        const count: number = _.size(this.locations)
        const countText: string = count > 0
            ? ` (${count})`
            : TypeHelper.emptyString

        //this.popup.option('title', `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.GroupsEdit)}${countText}`)
        if (this.popup != null && this.popup != null) {
            this.popup.getInnerPopup().option('title', `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.GroupsEdit)}${countText}`)
        }
    }

    private reset(): void {
        this.isLoading = true
        this.isPopupReady = false
        this.errorsSummaryVisible = false
        this.isStarted = false
        this.hasErrors = false
        this.isPopupClosing = true

        this.savingStartTime = null
        this.savingEndTime = null

        this.savedLocationsCount = 0
        this.uploadedPercent = 0

        this.currentProgressStep = null
        this.massGroupAssigningSummaryResult = null

        this.locations = []
    }

    private resetSaveProgress(): void {
        this.grid
            .getDataSource()
            .items()
            .forEach((row: any, index: number) => {
                row[LocationsMassGroupsEditController.SUCCESS_TEXT] = null
                row[LocationsMassGroupsEditController.WARNING_TEXT] = null
                row[LocationsMassGroupsEditController.ERROR_TEXT] = null

                this.grid.expandRow(false)

                this.grid.repaintRows([index])
            })

        this.gridController.clearItemDisplayStates()

        this.currentProgressStep = null
        this.massGroupAssigningSummaryResult = []

        this.savedLocationsCount = 0
        this.uploadedPercent = 0
        this.isStarted = true
        this.savingStartTime = performance.now()

        this.hideErrorsSummarySection()
    }

    // #endregion

    // #region Errors Summary

    private showErrorsSummarySection(): void {
        this.errorsSummaryVisible = true
    }

    private hideErrorsSummarySection(): void {
        this.errorsSummaryVisible = false
    }

    private initializeErrorsSummary(): JQueryPromise<void> {
        this.errorsSummaryOptions = {
            onInitialized: (errorsSummary: IIMRErrorsSummary) => this.errorsSummaryComponent = errorsSummary,
            onSelectErrorRows: (keys: number[]) => { this.filterGrid(keys) }
        }

        return TypeHelper.onInitializedAll(this.errorsSummaryOptions)
    }

    private filterGrid(keys: number[]): void {
        const filterExpr: ((string | number)[] | string)[] = []

        keys.forEach((key: number, index: number) => {
            filterExpr.push(["index", key])

            if (index !== keys.length - 1) {
                filterExpr.push("or")
            }
        })

        if (filterExpr.length > 0) {
            this.grid.getDataSource().filter(filterExpr)
            this.grid.getDataSource().load()
        } else {
            this.grid.clearFilter()
        }
    }

    private setSummaryOptions(summaryOptions: SummaryOptions): void {
        if (this.errorsSummaryComponent != null) {
            this.errorsSummaryComponent.setSummaryOptions(summaryOptions)
        }
    }

    // #endregion
}